'use client';

// Image de l'intro (« JeRaisonne présente ») et du générique : deux scènes
// ajoutées par le lecteur (`withBookends`), jamais écrites par l'IA (l'accroche
// de la vidéo a son propre calque : `HookLayer`). Calque
// opaque dans le repère du canevas, animé par le temps de la scène comme le
// reste (`apply(t)` dans la boucle d'affichage : pause, reprise, capture
// vidéo et mouvement réduit compris).
//
// Le logo est la boussole de `CompassLogo` (même dessin) : son aiguille fait
// le même tour amorti que dans l'app, mais réglé sur le temps de la scène
// (une animation CSS en boucle ne se mettrait ni en pause ni dans la vidéo).

import { forwardRef, useId, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import {
  JRAF_PALETTE,
  speakerCredit,
  type BookendKind,
  type OutroMessage,
} from '@jeraisonne/animation';
import { AnimationMathText } from './animation-math';
import { SpeakerAvatar, type SpeakerAvatarInfo } from './speaker-avatar';

export interface BookendHandle {
  apply(t: number): void;
}

type LayerKind = Exclude<BookendKind, 'hook'>;

export interface BookendLayerProps {
  kind: LayerKind;
  title: string;
  /** « 1ère · Physique-Chimie Spé » */
  meta: string | null;
  /** message du générique : au revoir (app) ou « Défie tes potes ! … » (vidéo) */
  message: OutroMessage;
  /** voix enregistrées (« Voix : … ») */
  speakers: readonly string[];
  /**
   * qui a enregistré les voix (avatar bouche fermée + prénom), côte à côte à
   * l'intro et au générique ; `caption` : le rôle tenu en dialogue (« le prof »)
   */
  hosts?: readonly BookendHost[];
  reducedMotion: boolean;
}

export interface BookendHost extends SpeakerAvatarInfo {
  caption?: string | null;
}

/** Diamètre des avatars selon leur nombre (3 tiennent dans 300 px). */
export function hostAvatarSize(count: number): number {
  return count <= 1 ? 64 : count === 2 ? 58 : 50;
}

type Part = 'mark' | 'word' | 'presents' | 'title' | 'meta' | 'punch' | 'line' | 'host' | 'credits';

/** apparition de chaque élément : [début, fin] en ms de scène */
const TIMING: Record<LayerKind, Partial<Record<Part, readonly [number, number]>>> = {
  intro: {
    mark: [0, 650],
    word: [500, 1050],
    presents: [950, 1400],
    title: [1350, 2000],
    meta: [1750, 2300],
    host: [2100, 2700],
  },
  outro: {
    mark: [0, 600],
    word: [300, 850],
    punch: [650, 1100],
    line: [1000, 1550],
    title: [1450, 1950],
    host: [1650, 2250],
    credits: [1850, 2400],
  },
};

/** début du tour de l'aiguille (ms de scène) */
const NEEDLE_START: Record<LayerKind, number> = { intro: 300, outro: 200 };

/**
 * Tour amorti de l'aiguille (mêmes étapes que `jr-compass-spin` de
 * CompassLogo, sur ses 2 premières secondes) : [ms, degrés].
 */
const NEEDLE_KEYS: readonly (readonly [number, number])[] = [
  [0, 0],
  [840, 188],
  [1320, 146],
  [1680, 172],
  [1860, 158],
  [1980, 164],
  [2040, 162],
];

/** Angle de l'aiguille `ms` après le début de son tour. */
export function needleAngle(ms: number): number {
  if (ms <= 0) return 0;
  for (let i = 1; i < NEEDLE_KEYS.length; i++) {
    const [t1, a1] = NEEDLE_KEYS[i]!;
    if (ms < t1) {
      const [t0, a0] = NEEDLE_KEYS[i - 1]!;
      const p = (ms - t0) / (t1 - t0);
      const e = p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2;
      return a0 + (a1 - a0) * e;
    }
  }
  return NEEDLE_KEYS[NEEDLE_KEYS.length - 1]![1];
}

function easeOut(p: number): number {
  return 1 - (1 - p) ** 3;
}

function outBack(p: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (p - 1) ** 3 + c1 * (p - 1) ** 2;
}

const C = JRAF_PALETTE;

function nameKey(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

/**
 * Le nom d'une voix est déjà écrit sous un avatar : même prénom, ou un diminutif
 * (« Phil » pour Philippe, nom donné à l'enregistrement).
 */
export function namedByHost(
  speaker: string,
  hosts: readonly { firstName: string | null }[],
): boolean {
  const k = nameKey(speaker);
  if (!k) return true;
  return hosts.some((h) => {
    const f = nameKey(h.firstName ?? '');
    return f !== '' && (f === k || (k.length >= 3 && f.startsWith(k)));
  });
}

export const BookendLayer = forwardRef<BookendHandle, BookendLayerProps>(function BookendLayer(
  { kind, title, meta, message, speakers, hosts = [], reducedMotion },
  ref,
) {
  const parts = useRef<Partial<Record<Part, HTMLElement | null>>>({});
  // « Voix : Philippe et une voix de synthèse » (la synthèse est toujours dite)
  // les prénoms des avatars sont écrits dessous : le crédit nomme les autres voix
  const credit = speakerCredit(speakers.filter((s) => !namedByHost(s, hosts)));
  const hostKey = hosts.map((h) => `${h.firstName ?? ''}:${h.frames[0] ?? ''}`).join('|');
  const needle = useRef<SVGSVGElement | null>(null);
  const reg = (p: Part) => (el: HTMLElement | null) => {
    parts.current[p] = el;
  };

  const lastT = useRef(0);
  const apply = (t: number) => {
    lastT.current = t;
    const time = reducedMotion ? Number.POSITIVE_INFINITY : t;
    for (const [part, span] of Object.entries(TIMING[kind]) as [Part, [number, number]][]) {
      const el = parts.current[part];
      if (!el) continue;
      const p = Math.min(1, Math.max(0, (time - span[0]) / (span[1] - span[0])));
      el.style.opacity = String(Math.round(easeOut(p) * 1000) / 1000);
      el.style.transform =
        part === 'mark' || part === 'host'
          ? `scale(${Math.round((0.6 + 0.4 * (p >= 1 ? 1 : outBack(p))) * 1000) / 1000})`
          : part === 'punch'
            ? // l'accroche du générique arrive en bondissant
              `scale(${Math.round((0.4 + 0.6 * (p >= 1 ? 1 : outBack(p))) * 1000) / 1000})`
            : `translateY(${Math.round((1 - easeOut(p)) * 14 * 10) / 10}px)`;
    }
    if (needle.current) {
      const a = Math.round(needleAngle(time - NEEDLE_START[kind]) * 10) / 10;
      needle.current.style.transform = `translate(-50%, -50%) rotate(${a}deg)`;
    }
  };
  useImperativeHandle(ref, () => ({ apply }));
  // styles posés par `apply` (React ne les touche pas) : seulement au montage
  // et quand la mise en page change (un élément de plus, mouvement réduit)
  useLayoutEffect(
    () => apply(lastT.current),
    [kind, reducedMotion, meta, credit, message.punch, hostKey],
  );

  const big = kind === 'intro';
  return (
    <div
      data-bookend={kind}
      className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center"
      style={{
        backgroundColor: C.night,
        backgroundImage:
          'radial-gradient(circle at 50% 34%, rgba(5,102,242,0.38) 0, rgba(5,102,242,0) 58%)',
        color: C.paper,
      }}
    >
      <div
        ref={reg('mark')}
        aria-hidden="true"
        className="relative rounded-full"
        style={{
          width: big ? 112 : 84,
          height: big ? 112 : 84,
          boxShadow: '0 18px 44px rgba(4,160,254,0.42)',
        }}
      >
        <BrandCompassDial />
        <svg
          ref={needle}
          viewBox="0 0 456 461"
          fill="none"
          className="absolute left-1/2 top-1/2"
          style={{
            width: '44.5%',
            height: '45%',
            transform: 'translate(-50%, -50%)',
            transformOrigin: '50% 50%',
          }}
        >
          <path
            d="M421.561 16.1416C433.35 10.6159 445.638 22.767 440.245 34.6175L313.043 314.109C312.048 316.296 310.298 318.052 308.114 319.055L36.7886 443.611C24.1399 449.418 11.0897 436.364 16.9004 423.717L142.36 150.657C143.742 147.65 146.144 145.229 149.14 143.825L421.561 16.1416Z"
            fill="white"
          />
          <ellipse cx="228" cy="230.5" rx="56.5" ry="55" fill="#0581F7" />
        </svg>
      </div>
      <p
        ref={reg('word')}
        className="font-display mt-5 font-extrabold leading-none tracking-tight"
        style={{ fontSize: big ? 40 : 31 }}
      >
        JeRaisonne
      </p>

      {kind === 'intro' ? (
        <>
          <p
            ref={reg('presents')}
            className="mt-3 text-[14px] font-semibold uppercase tracking-[0.28em]"
            style={{ color: C['primary-light'] }}
          >
            présente
          </p>
          <p
            ref={reg('title')}
            className="font-display mt-12 max-w-[300px] text-[30px] font-bold leading-[1.15]"
            style={{ color: C.gold }}
          >
            <AnimationMathText>{title}</AnimationMathText>
          </p>
          {meta && (
            <p
              ref={reg('meta')}
              className="mt-6 rounded-full px-4 py-1.5 text-[15px] font-semibold"
              style={{
                border: '1.5px solid rgba(255,255,255,0.35)',
                color: 'rgba(255,255,255,0.92)',
              }}
            >
              {meta}
            </p>
          )}
          {hosts.length > 0 && <HostRow register={reg('host')} hosts={hosts} className="mt-9" />}
        </>
      ) : (
        <>
          {message.punch ? (
            <>
              <p
                ref={reg('punch')}
                className="font-display mt-11 max-w-[310px] text-[34px] font-extrabold leading-[1.1]"
                style={{ color: C.gold, textShadow: '0 0 24px rgba(242,193,78,0.35)' }}
              >
                {message.punch}
              </p>
              <p
                ref={reg('line')}
                className="font-display mt-3 max-w-[290px] text-[22px] font-bold leading-[1.25]"
              >
                {message.line}
              </p>
            </>
          ) : (
            <p
              ref={reg('line')}
              className="font-display mt-12 max-w-[300px] text-[27px] font-bold leading-[1.2]"
              style={{ color: C.gold }}
            >
              {message.line}
            </p>
          )}
          <p
            ref={reg('title')}
            className="mt-6 max-w-[300px] text-[16px] leading-snug"
            style={{ color: 'rgba(255,255,255,0.78)' }}
          >
            <AnimationMathText>{title}</AnimationMathText>
          </p>
          {hosts.length > 0 && <HostRow register={reg('host')} hosts={hosts} className="mt-6" />}
          {credit && (
            <p
              ref={reg('credits')}
              className={
                hosts.length ? 'mt-3 text-[15px] font-semibold' : 'mt-8 text-[15px] font-semibold'
              }
              style={{ color: C['primary-light'] }}
            >
              {credit}
            </p>
          )}
        </>
      )}
    </div>
  );
});

/** Avatars de qui a dit les voix, côte à côte, prénom (et rôle) dessous. */
function HostRow({
  hosts,
  register,
  className,
}: {
  hosts: readonly BookendHost[];
  register: (el: HTMLElement | null) => void;
  className?: string;
}) {
  const size = hostAvatarSize(hosts.length);
  return (
    <div ref={register} data-bookend-host="" className={className}>
      <p
        className="mb-2 text-[12px] font-semibold uppercase tracking-[0.22em]"
        style={{ color: 'rgba(255,255,255,0.7)' }}
      >
        {hosts.length > 1 ? 'Les voix' : 'La voix'}
      </p>
      <div className="flex items-start justify-center gap-5">
        {hosts.map((h, i) => (
          <div
            key={`${h.frames[0] ?? i}-${i}`}
            data-bookend-host-item=""
            className="flex w-[84px] flex-col items-center"
          >
            <SpeakerAvatar frames={h.frames} frame={0} size={size} />
            {h.firstName && (
              <p className="mt-2 text-[15px] font-bold leading-tight" style={{ color: C.paper }}>
                {h.firstName}
              </p>
            )}
            {h.caption && (
              <p className="mt-0.5 text-[12px] leading-tight" style={{ color: C['primary-light'] }}>
                {h.caption}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cadran de la boussole JeRaisonne (même dessin que `CompassLogo`). */
function BrandCompassDial() {
  const id = `jr-dial-${useId().replace(/:/g, '')}`;
  return (
    <svg viewBox="0 0 1024 1024" fill="none" className="absolute inset-0 h-full w-full">
      <defs>
        <linearGradient id={id} x1="104" y1="162" x2="894" y2="779" gradientUnits="userSpaceOnUse">
          <stop stopColor="#04A0FE" />
          <stop offset="1" stopColor="#0566F2" />
        </linearGradient>
      </defs>
      <rect width="1024" height="1024" rx="512" fill={`url(#${id})`} />
      <path
        d="M512 105.5C737.126 105.5 919.5 287.566 919.5 512C919.5 736.434 737.126 918.5 512 918.5C286.874 918.5 104.5 736.434 104.5 512C104.5 287.566 286.874 105.5 512 105.5Z"
        stroke="white"
        strokeWidth="65"
      />
      <rect x="496" y="73" width="32" height="107" rx="16" fill="white" />
      <rect x="496" y="844" width="32" height="107" rx="16" fill="white" />
      <rect x="845" y="496" width="107" height="32" rx="16" fill="white" />
      <rect x="72" y="496" width="107" height="32" rx="16" fill="white" />
    </svg>
  );
}
